import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRegisterLayerReload } from '@/shared/lib/layerReload';
import { Skeleton } from '@/shared/ui/skeleton';

import type {
  ListFilterFieldDef,
  ListPageConfig,
  ListStatDef,
  ResolvedListPageConfig,
} from './model/types';
import { defaultPinnedFilters } from './model/filterModel';
import { useListPageController, type ListPageInitialState } from './model/useListPageController';
import { useListPageViews } from './model/useListPageViews';
import { ALL_VIEW_ID, buildViews } from './model/viewsModel';
import {
  applyDisplayRefs,
  decodeDisplayPayload,
  decodeUserState,
  decodeViewPayload,
} from './persistence/payload';
import { usePersistUserState } from './persistence/usePersistUserState';
import { useSelectorRefRevalidation } from './persistence/useSelectorRefRevalidation';
import {
  useListPageServerState,
  type ListPageServerState,
} from './persistence/useListPageServerState';
import { BulkBar } from './regions/BulkBar';
import { ChipsRow } from './regions/ChipsRow';
import { FilterPanel } from './regions/FilterPanel';
import { ListToolbar } from './regions/ListToolbar';
import { LoadError, LoadingOverlay, WaitingForSearch } from './regions/ListStates';
import { PagerFooter } from './regions/PagerFooter';
import { PreviewDrawer } from './regions/PreviewDrawer';
import { StatsStrip } from './regions/StatsStrip';
import { ViewsBar } from './regions/ViewsBar';
import { ListTable } from './table/ListTable';

/**
 * Univerzální ListPage (anatomie ListPage 01–05) — jedna implementace chování, entitně
 * specifické věci dodává šablona přes `defineListPage`. Otevření agendy čeká na centrální
 * persistenci (`GET /api/list-pages/{key}/state`) a obnoví naposledy aktivní pohled,
 * konfiguraci sloupců i preference; první dotaz se spouští až po aplikaci stavu.
 */
/** Picker režim ListPage — řádky se vybírají (toggle), bez draweru/bulk lišty/akcí řádku. */
export interface ListPagePickerOptions {
  initialSelection: (string | number)[];
  onSelectionChange: (ids: (string | number)[]) => void;
}

export function ListPage<TItem, TStats = unknown>({
  config,
  picker,
  initialViewId,
  onActiveViewChange,
}: {
  config: ListPageConfig<TItem, TStats>;
  picker?: ListPagePickerOptions;
  /** Počáteční aktivní pohled (z URL). Když chybí, otevře se první záložka („Vše"). */
  initialViewId?: string;
  /** Zavolá se při změně aktivního pohledu — most (`NavListPage`) ho promítne do URL. */
  onActiveViewChange?: (viewId: string) => void;
}) {
  const { t } = useTranslation(config.i18n?.namespace);

  const resolved = useMemo<ResolvedListPageConfig<TItem>>(
    () => ({
      key: config.key,
      api: { list: config.api.list },
      rowKey: config.rowKey,
      columns: config.columns(t),
      filters: config.filters?.(t) ?? [],
      filterGroups: config.filterGroups?.(t) ?? [],
      scope: config.scope,
      defaultSort: config.defaultSort,
      fulltextPlaceholder: config.fulltext?.placeholder?.(t),
      autoLoad: config.autoLoad !== false,
      templateViews: config.views?.(t) ?? [],
      statsApi: config.api.stats,
      statsDefs: config.stats?.(t) as ListStatDef<unknown>[] | undefined,
      exportApi: config.api.export,
      exportFileName: config.exportFileName?.(t),
      preview: config.preview ? (item: TItem) => config.preview!(item, t) : undefined,
      bulkActions: config.bulkActions?.(t),
      onOpen: config.onOpen,
      create: config.create
        ? { label: config.create.label?.(t), onClick: config.create.onClick }
        : undefined,
    }),
    [config, t]
  );

  const server = useListPageServerState(resolved.key);

  if (server.isPending) {
    return (
      <div className="phxlist p-phx-5 gap-phx-4 flex flex-col">
        <Skeleton className="h-phx-control-lg w-[420px]" />
        <Skeleton className="h-phx-control-lg w-full" />
        <Skeleton className="min-h-[200px] w-full flex-1" />
      </div>
    );
  }

  // Chyba načtení stavu → výchozí stav šablony (persistence nesmí blokovat práci).
  return (
    <ListPageInner
      resolved={resolved}
      server={server}
      picker={picker}
      initialViewId={initialViewId}
      onActiveViewChange={onActiveViewChange}
    />
  );
}

function ListPageInner<TItem>({
  resolved,
  server,
  picker,
  initialViewId,
  onActiveViewChange,
}: {
  resolved: ResolvedListPageConfig<TItem>;
  server: ListPageServerState;
  picker?: ListPagePickerOptions;
  initialViewId?: string;
  onActiveViewChange?: (viewId: string) => void;
}) {
  const defs = useMemo(
    () => new Map<string, ListFilterFieldDef>(resolved.filters.map((def) => [def.key, def])),
    [resolved.filters]
  );

  // Přišpendlené filtry ze specifikace (příznak `pinned`) — nasazují se na pohled „Vše".
  const allViewFilters = useMemo(() => defaultPinnedFilters(resolved.filters), [resolved.filters]);

  // Stav z centrální persistence se aplikuje jednorázově při mountu (initializery useState).
  const [initial] = useState<ListPageInitialState>(() => {
    const decoded = decodeUserState(server.userStatePayload, resolved.columns);
    const views = buildViews({
      allLabel: '',
      templateViews: resolved.templateViews,
      serverViews: server.views.map((view) => ({
        id: view.id,
        name: view.name,
        shared: view.shared,
        own: view.own,
        filters: decodeViewPayload(view.payload, defs),
        displayRefs: decodeDisplayPayload(view.displayPayload),
      })),
      viewOrder: decoded.viewOrder,
      allFilters: allViewFilters,
    });
    // Aktivní pohled řídí URL (`initialViewId`), ne serverová persistence: reload/deeplink obnoví
    // uložený pohled, čerstvé otevření z menu/navigátoru (bez `view` v URL) začne na „Vše".
    const savedView = initialViewId ? views.find((view) => view.id === initialViewId) : undefined;
    const activeView = savedView ?? views.find((view) => view.id === ALL_VIEW_ID);

    return {
      filters: activeView
        ? applyDisplayRefs(activeView.filters, activeView.displayRefs, defs)
        : undefined,
      activeViewId: activeView?.id,
      // Obnova uloženého pohledu = explicitní vyhledání; „Vše" bez uloženého stavu ctí autoLoad
      // (stav „čeká na vyhledání" zůstává, anatomie 05 §6).
      searchOnInit: savedView ? true : resolved.autoLoad,
      columns: decoded.columns?.items,
      widthMode: decoded.columns?.widthMode,
      selection: picker?.initialSelection,
      viewOrder: decoded.viewOrder,
      prefs: decoded.prefs,
    };
  });

  const controller = useListPageController(resolved, initial);

  // Frameworkový reload okna přenačte seznam přesně přes controller (zachová filtry/pohled/stránkování),
  // místo remountu, který by tento stav zahodil. V picker režimu neregistrujeme — ListPage je pak
  // vnořený v cizí vrstvě a nesměl by přebít její reload handler.
  useRegisterLayerReload(picker ? undefined : () => controller.refetch());

  // Aktivní pohled promítni do URL přes most (`onActiveViewChange`). Ref → efekt necitlivý na identitu
  // callbacku; běží při mountu i změně aktivního pohledu. Bez mostu (picker) je to no-op.
  const onActiveViewChangeRef = useRef(onActiveViewChange);
  onActiveViewChangeRef.current = onActiveViewChange;
  const activeViewId = controller.activeViewId;
  useEffect(() => {
    onActiveViewChangeRef.current?.(activeViewId);
  }, [activeViewId]);

  const viewsApi = useListPageViews({
    controller,
    config: resolved,
    server,
    defs,
    allFilters: allViewFilters,
  });

  usePersistUserState({ controller, server });
  useSelectorRefRevalidation({ controller, server, defs });

  // Picker režim hlásí výběr ven (footer modalu počítá z něj a potvrzuje).
  const onSelectionChange = picker?.onSelectionChange;
  const { selection } = controller;
  useEffect(() => {
    onSelectionChange?.([...selection]);
  }, [selection, onSelectionChange]);

  const pickerMode = picker !== undefined;

  // Pohledy a toolbar drží celou šířku nahoře; pod nimi je pracovní oblast jako řádek —
  // vlevo panel filtrů přes celou výšku, vpravo sloupec s tabulkou a patičkou stránkování
  // (panel tak sousedí s tabulkou i patičkou).
  return (
    <div className="phxlist">
      <div className="phxlist-filterzone">
        <ViewsBar views={viewsApi} />
        <ChipsRow controller={controller} />
      </div>
      <ListToolbar
        controller={controller}
        columns={resolved.columns}
        fulltextPlaceholder={resolved.fulltextPlaceholder}
        hasStats={(resolved.statsDefs?.length ?? 0) > 0}
        hasExport={resolved.exportApi !== undefined}
        exportFileName={resolved.exportFileName}
        onCreate={pickerMode ? undefined : resolved.create?.onClick}
        createLabel={resolved.create?.label}
      />
      <StatsStrip controller={controller} defs={resolved.statsDefs} />
      <div className="phxlist-main">
        <FilterPanel
          controller={controller}
          groups={resolved.filterGroups}
          fields={resolved.filters}
        />
        <div className="phxlist-rightcol">
          <div className="phxlist-listcol">
            {!controller.searched ? (
              <WaitingForSearch onSearch={controller.search} loading={controller.isLoading} />
            ) : controller.isError ? (
              <LoadError onRetry={controller.refetch} />
            ) : (
              <>
                <ListTable
                  controller={controller}
                  columns={resolved.columns}
                  pickerMode={pickerMode}
                />
                {controller.isLoading ? <LoadingOverlay /> : null}
              </>
            )}
          </div>
          <PagerFooter controller={controller} />
        </div>
      </div>
      {pickerMode ? null : (
        <>
          <PreviewDrawer controller={controller} render={resolved.preview} />
          <BulkBar controller={controller} actions={resolved.bulkActions} />
        </>
      )}
    </div>
  );
}
